import React, { useCallback, useEffect, useState } from 'react';
import { parseHmsInputStrings, secondsToHmsParts } from '../../utils/durationInput';

export interface DurationHmsFieldProps {
  /** Field label (e.g. "Runtime", "Duration") */
  label: string;
  /** Total seconds from parent; `undefined`/`null` when optional and empty */
  valueSeconds?: number | null;
  onSecondsChange: (value: number | undefined) => void;
  required?: boolean;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  /** Prefix for input ids (defaults to "duration-hms") */
  idPrefix?: string;
}

const inputClass =
  'w-full min-w-0 rounded-lg border border-gray-300 px-3 py-2 text-base text-gray-900 bg-white focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * Three-part duration entry (hours : minutes : seconds). Parent stores total seconds.
 */
export const DurationHmsField: React.FC<DurationHmsFieldProps> = ({
  label,
  valueSeconds,
  onSecondsChange,
  required = false,
  error,
  helperText,
  disabled = false,
  idPrefix = 'duration-hms',
}) => {
  const [hoursStr, setHoursStr] = useState('');
  const [minutesStr, setMinutesStr] = useState('');
  const [secondsStr, setSecondsStr] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    if (valueSeconds === undefined || valueSeconds === null) {
      if (!required) {
        setHoursStr('');
        setMinutesStr('');
        setSecondsStr('');
        setParseError(null);
        return;
      }
      const z = secondsToHmsParts(0);
      setHoursStr(String(z.hours));
      setMinutesStr(String(z.minutes));
      setSecondsStr(String(z.seconds));
      setParseError(null);
      return;
    }
    const p = secondsToHmsParts(valueSeconds);
    setHoursStr(String(p.hours));
    setMinutesStr(String(p.minutes));
    setSecondsStr(String(p.seconds));
    setParseError(null);
  }, [valueSeconds, required]);

  const pushParsed = useCallback(
    (nextH: string, nextM: string, nextS: string) => {
      const parsed = parseHmsInputStrings(nextH, nextM, nextS, {
        allowAllEmpty: !required,
      });
      if (!parsed.ok) {
        setParseError(parsed.message);
        return;
      }
      setParseError(null);
      onSecondsChange(parsed.seconds);
    },
    [onSecondsChange, required]
  );

  const combinedError = error || parseError;

  return (
    <div className="w-full text-left">
      <fieldset className="border-0 p-0 m-0 min-w-0">
        <legend className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
          {required ? <span className="text-red-500 ml-0.5">*</span> : null}
        </legend>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[4.5rem]">
            <label htmlFor={`${idPrefix}-h`} className="sr-only">
              Hours
            </label>
            <input
              id={`${idPrefix}-h`}
              type="number"
              min={0}
              inputMode="numeric"
              disabled={disabled}
              value={hoursStr}
              onChange={(e) => {
                const v = e.target.value;
                setHoursStr(v);
                pushParsed(v, minutesStr, secondsStr);
              }}
              className={inputClass}
              placeholder="0"
            />
            <span className="text-xs text-gray-500 mt-0.5 block">Hours</span>
          </div>
          <span className="pb-2 text-gray-500 font-medium" aria-hidden="true">
            :
          </span>
          <div className="flex-1 min-w-[4.5rem]">
            <label htmlFor={`${idPrefix}-m`} className="sr-only">
              Minutes
            </label>
            <input
              id={`${idPrefix}-m`}
              type="number"
              min={0}
              max={59}
              inputMode="numeric"
              disabled={disabled}
              value={minutesStr}
              onChange={(e) => {
                const v = e.target.value;
                setMinutesStr(v);
                pushParsed(hoursStr, v, secondsStr);
              }}
              className={inputClass}
              placeholder="0"
            />
            <span className="text-xs text-gray-500 mt-0.5 block">Minutes</span>
          </div>
          <span className="pb-2 text-gray-500 font-medium" aria-hidden="true">
            :
          </span>
          <div className="flex-1 min-w-[4.5rem]">
            <label htmlFor={`${idPrefix}-s`} className="sr-only">
              Seconds
            </label>
            <input
              id={`${idPrefix}-s`}
              type="number"
              min={0}
              max={59}
              inputMode="numeric"
              disabled={disabled}
              value={secondsStr}
              onChange={(e) => {
                const v = e.target.value;
                setSecondsStr(v);
                pushParsed(hoursStr, minutesStr, v);
              }}
              className={inputClass}
              placeholder="0"
            />
            <span className="text-xs text-gray-500 mt-0.5 block">Seconds</span>
          </div>
        </div>
        {combinedError ? (
          <p className="text-sm text-red-600 mt-1.5" role="alert">
            {combinedError}
          </p>
        ) : null}
        {helperText && !combinedError ? (
          <p className="text-xs text-gray-500 mt-1.5">{helperText}</p>
        ) : null}
      </fieldset>
    </div>
  );
};

export default DurationHmsField;
